/* ─────────────────────────────────────────────────────────────
   Plan2BenefitCarVersion / data.js

   Content for the Car Plan homepage — "The Spec Sheet" concept.
   Quiet, numbers-led copy in the same language a car's own spec
   sheet uses. All figures and copy below are the approved,
   verbatim page content; keep this file as the single source of
   truth for it rather than hardcoding strings in components.

   A literal "*" inside any string is a footnote marker — render
   it through <WithStar /> rather than stripping or repositioning
   it, since it isn't always at the end of the sentence.
───────────────────────────────────────────────────────────── */

export const heroPromises = [
  "Get Your First EMI Free*",
  "Save Your Down Payment & Earn Interest",
  "No Pre-Booking Amount",
];

export const proofStats = [
  { to: 20, prefix: "₹", suffix: " Cr+", label: "Car Down Payment Savings" },
  { to: 100, prefix: "₹", suffix: " Cr+", label: "Loans Disbursed*" },
  { to: 25, suffix: "+", label: "Lending Partners" },
  { to: 500, suffix: "+", label: "Dealer Network*" },
];

export const journeySteps = [
  {
    id: "choose-car",
    title: "Choose Your Car",
    description: "Tell us your budget and needs. AI helps you choose.",
    tag: "Day 1",
  },
  {
    id: "test-drive",
    title: "Test Drive at Home",
    description: "Pick a time. We bring the test drive to you.*",
    tag: "At your door",
  },
  {
    id: "down-payment",
    title: "Plan Your Down Payment",
    description: "Build your Car Fund and earn applicable interest.*",
    tag: "Ongoing",
  },
  {
    id: "loan",
    title: "Get Your Loan",
    description: "Compare 25+ eligible lenders and choose your offer.",
    tag: "25+ lenders",
  },
  {
    id: "benefits",
    title: "Get Your Benefits",
    description: "First EMI benefit* + eligible dealer, sale and festival offers.",
    tag: "Stacked",
  },
  {
    id: "car-day",
    title: "Choose Your Car Day",
    description: "Pick your Muhurat, occasion or date and we'll help plan towards it.*",
    tag: "Your date",
  },
  {
    id: "drive-home",
    title: "Drive Home",
    description: "From paperwork to delivery, we coordinate the journey.",
    tag: "Done",
    arrive: true,
  },
];

export const carLoanBenefits = [
  { id: "bank", label: "Any Bank" },
  { id: "car", label: "Any Car Brand & Model" },
  { id: "loan-amount", label: "Any Car Loan Amount" },
  { id: "emi-amount", label: "Any EMI Amount" },
];

export const comparisonRows = [
  { label: "Getting there", without: "Showroom hopping", withUs: "One Car Plan, from home" },
  { label: "Financing", without: "Loan hunting", withUs: "25+ lenders compared for you" },
  { label: "The math", without: "Confusing calculations", withUs: "Clear down payment & EMI picture" },
  { label: "Paperwork", without: "Unnecessary paperwork trips", withUs: "Coordinated, digital, done" },
];

export const cxNote =
  "This directly aligns with the current CX evidence: intuitive digital experience, financing, test-drive convenience, seamless documentation and transparent communication are meaningful parts of the Indian passenger-vehicle journey.";

export const faqs = [
  {
    question: "How does Plan2Benefit work?",
    answer:
      "Choose your car, plan your down payment, compare eligible loans, get your benefits and coordinate delivery — all through one Car Plan.",
  },
  {
    question: "Where is my down-payment money kept?",
    answer:
      "Your eligible Car Fund is placed with Suryoday Small Finance Bank through the applicable banking product.",
  },
  {
    question: "Will my down payment earn interest?",
    answer:
      "Yes. Your eligible Car Fund earns the applicable interest rate offered by Suryoday Small Finance Bank, which is shown in the Plan2Benefit app before you proceed.",
  },
  {
    question: "How does First EMI Free work?",
    answer:
      "After your eligible car loan is successfully disbursed by the bank/NBFC, your eligible First EMI benefit is transferred directly to your registered bank account.*",
  },
  {
    question: "Do I have to pay my EMI to the bank?",
    answer:
      "Yes. Your loan and EMI remain your responsibility to the lender. The Plan2Benefit benefit is transferred separately to your bank account.",
  },
  {
    question: "Which banks and NBFCs can I choose from?",
    answer:
      "You can compare eligible offers from 25+ participating banks and NBFCs available for your profile and car.",
  },
  {
    question: "Do I need to visit the showroom?",
    answer:
      "Not necessarily. Where available, we can arrange your free home test drive and coordinate much of the purchase journey from home.",
  },
  {
    question: "Do I need to pay a pre-booking amount?",
    answer:
      "For eligible participating dealers and offers, no pre-booking amount is required.",
  },
  {
    question: "Can I choose my delivery date or Muhurat?",
    answer:
      "Yes. Choose your preferred date, Muhurat or occasion, and we'll coordinate the journey towards your planned delivery date.*",
  },
  {
    question: "What if I change my mind?",
    answer:
      "You can change your car or purchase date, or cancel your plan, subject to the applicable Car Plan and banking-product terms.",
  },
];

export const faqDisclaimer =
  "First EMI benefit, interest rates, loan approval, home test drive, no-pre-booking availability and delivery are subject to eligibility, partner/dealer availability and applicable terms.";
